import { Component, Input, OnInit, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DashboardComponent } from '../../models/DashboardComponent';
import { DashboardDatatableOptions,DatatableColumn,DatatableOptions } from '../../models/DashboardDatatableOptions';
import * as _ from 'lodash-es';
import { WorkspaceService } from 'projects/gtw-elements/src/app/workspace/workspace.service';
import { DEFAULT_DATATABLE_CONFIG } from '../../global-defaults/default-datatable-options';
import { DataFormatterService } from '../../services/data-formatter.service';
import { HttpClient } from '@angular/common/http';


@Component({
    selector: 'gtw-ex-datatable',
    templateUrl: './ex-datatable.component.html',
    styleUrls: ['./ex-datatable.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ExDatatableComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

    @Input()
    options!: DashboardComponent;

    componentOptions!: DashboardDatatableOptions;
    tableOptions!: DatatableOptions;
    childTableOptions!: DatatableOptions;
    filterParams: any;
    activeScreen: any;
    sheetData!: any[];
    rowClass: any;

    expandedRow: any = null;

    get displayedColumns(): string[] {
        return (this.tableOptions?.columns || []).map((col: DatatableColumn) => col.columnKey!);
    }

    get childDisplayedColumns(): string[] {
        return (this.childTableOptions?.columns || []).map((col: DatatableColumn) => col.columnKey!);
    }

    isExpandedRow = (_index: number, row: any): boolean => this.expandedRow === row;

    constructor(private workspaceService: WorkspaceService,
        private dataFormatterFactory: DataFormatterService,
        private http: HttpClient) {
        this.activeScreen = this.workspaceService.workspaceFactory.activeScreen;
     }

    ngOnInit(): void {
        this.initOptions();
     }

    initOptions(){
        this.componentOptions = this.options.extraOptions.componentSettings as DashboardDatatableOptions;
        this.componentOptions.grouping = _.isUndefined(this.componentOptions.grouping)? false: this.componentOptions.grouping;
        this.componentOptions.amountColumns = this.componentOptions.amountColumns || [];
        this.componentOptions.nonAmountColumns = this.componentOptions.nonAmountColumns || [];
        this.sheetData = [...this.options.data];

        this.activeScreen && this.activeScreen.filters && (this.filterParams = this.activeScreen.filters.getFilterParams());

        this.tableOptions =  _.merge(_.cloneDeep(DEFAULT_DATATABLE_CONFIG), this.options.extraOptions.componentSettings.tableOptions);

        _.remove(this.tableOptions.columns, function (item: DatatableColumn) {
            return item.groupIndicator;
        });

        this.resolveColumnTitles(this.tableOptions.columns);
        this.assignColumnKeys(this.tableOptions.columns);

        this.tableOptions.data = this.sheetData;

        if (this.componentOptions.childTableOptions) {

            this.childTableOptions = _.cloneDeep(_.merge(_.cloneDeep(DEFAULT_DATATABLE_CONFIG), this.componentOptions.childTableOptions));
            this.assignColumnKeys(this.childTableOptions.columns);
            // make a copy of the ajax url in case of a template url is sent for child table
            // this.componentOptions.childTableOptions.ajax && this.componentOptions.childTableOptions.ajax.url && (this.componentOptions.childTableOptions.ajaxUrlCopy = this.componentOptions.childTableOptions.ajax.url);
        }

        this.setupFormatting(this.tableOptions);

        if(this.componentOptions.grouping){
            this.tableOptions.data = this.groupBy(this.sheetData, this.componentOptions.groupColumnsBy, this.componentOptions.nonAmountColumns, this.componentOptions.amountColumns,this.tableOptions.columns);
            this.setupFormatting(this.childTableOptions);
        }
    }
    /**
     * `matColumnDef` needs a unique, non-null id per column. Control columns
     * (row expander, drill-down) are configured with `data: null`, and a config
     * can repeat a data field, so fall back to an index-based id in both cases.
     */
    private assignColumnKeys(columns: DatatableColumn[]): void {
        if (!columns) {
            return;
        }
        const usedKeys = new Set<string>();
        columns.forEach((col, index) => {
            let key = col.data ? String(col.data) : `__col_${index}`;
            if (usedKeys.has(key)) {
                key = `${key}_${index}`;
            }
            usedKeys.add(key);
            col.columnKey = key;
        });
    }

    /**
     * The row expander cell is flagged through the column's class name:
     * newer dashboard configs use `details-control`, older ones use `n`.
     */
    isExpanderColumn(col: DatatableColumn): boolean {
        return this.hasClass(col, 'details-control') || this.hasClass(col, 'n');
    }

    isDrillDownColumn(col: DatatableColumn): boolean {
        return this.hasClass(col, 'drill-down');
    }

    private hasClass(col: DatatableColumn, className: string): boolean {
        return (col?.className || '').split(/\s+/).includes(className);
    }

    private setupFormatting(tableOptions: DatatableOptions) {
        if (tableOptions && tableOptions.columns && tableOptions.columns.length > 0) {
            tableOptions.columns.forEach((col, index) => {
                if (col.formatAmount || col.format) {
                    var formatter = this.dataFormatterFactory.getFormatter(col.format) || (col.formatAmount ? this.dataFormatterFactory.getFormatter('Amount') : null);

                    col.formatAmount && (col.format = 'Amount');
                    col.formatFn = formatter?.func;
                }
            });
        }
    }

    groupBy(sheetData: any[], groupColumnsBy: string, nonAmountColumns: string[], amountColumns: string[], columns: DatatableColumn[]): any[] {
        return _.map(_.groupBy(sheetData, groupColumnsBy), (groupedData: any, key:string) => {
            var dataObj: any = {
                children: groupedData
            }
            nonAmountColumns.forEach((col) => {
                let column: DatatableColumn = {..._.find(columns,["data",col])} as DatatableColumn;

                if(column.aggregator !== 'count'){
                    dataObj[col] = groupedData[0][col];
                    return;
                }

                dataObj[col] = groupedData.length;

            })

            if(this.componentOptions.defaultOrderby){
                //Add the order column to the dataset
                dataObj[this.componentOptions.defaultOrderby] = groupedData[0][this.componentOptions.defaultOrderby];
            }

            var booleanCols = _.filter(_.keys(groupedData[0]), (item)=>item.length > 4 && item.substr(0,4).toLowerCase() === "has_");//All booleans/bit should start with 'has_'

            booleanCols.forEach(function (col) {
                dataObj[col] = groupedData[0][col]
            })


            amountColumns.forEach((col) => {
                let column: DatatableColumn = {..._.find(columns,["data",col])} as DatatableColumn;

                const mappedValues = _.map(groupedData, col);
                const aggregator = (column ? column.aggregator : 'sum') || 'sum';
                const aggregators: Record<string, (values: any[]) => number> = { sum: _.sum };
                dataObj[col] = (aggregators[aggregator] ?? _.sum)(mappedValues);
                dataObj[col] = _.sumBy(groupedData, col)
            })
            return dataObj;

        })
    }


    hasRowStyling(className: string, data: any){
        if(this.options.extraOptions.componentSettings.conditionalRowStyling && this.isValidExpression(this.options.extraOptions.componentSettings.rowStylingCondition)){
            if(!this.options.extraOptions.componentSettings.rowStylingClassName.includes(className)){
                return;
            }
            const expression: string = this.options.extraOptions.componentSettings.rowStylingCondition;

            const matches = expression.match(/\{(.*?)}/g);

            if(!matches || matches.length === 0){
                return false;
            }

            let token: string = matches ? matches[0] : '';

            if(!token){
                return false;
            }

            let value: string = '';
            if(expression.includes("includes")){
                value = expression.replace(`"${token}".includes("`,'').replace(`")`,'');
                return data[token.replace('{','').replace('}','')].includes(value);
            }

        }

        return false;
    }

    isValidExpression (expression: string): boolean {
        return expression.includes("includes");
    }

    /**
     * Expand / collapse is driven off the button's own click output rather than
     * `pRowToggler`, which relies on a host click listener on the element it sits on.
     */
    toggleRow(rowData: any, event: Event){
        event?.stopPropagation?.();
        if (this.expandedRow === rowData) {
            this.expandedRow = null;
            return;
        }
        this.expandedRow = rowData;
        this.onRowExpand({ data: rowData });
    }

    onRowExpand(e: any){
        if(this.componentOptions.showChildRows && this.childTableOptions.isAjax && !e.data.children){
            let url = this.childTableOptions.ajax.url.replace("{project_id}",e.data.project_id);
            url = url.replace("{project_step_id}",e.data.project_step_id);
            e.data.childLoading = true;
            this.http.get<any>(url).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((response: any) => {
                e.data.children = response[this.childTableOptions.ajax.dataProp];
                e.data.childLoading = false;
            });

        }
    }

    /**
     * Resolve template variables in column titles.
     * Patterns like `Dec_year1` are replaced with the actual tax year,
     * `Dec_year2` with previous year, etc. Remaining underscores in titles
     * are converted to spaces (matching AngularJS behavior).
     */
    private resolveColumnTitles(columns: DatatableColumn[]): void {
        if (!columns || !this.filterParams) return;

        const taxYear = parseInt(this.filterParams.tax_year, 10);
        if (isNaN(taxYear)) return;

        columns.forEach(col => {
            if (!col.title) return;

            // Replace Dec_yearN patterns: Dec_year1 → current year, Dec_year2 → previous year, etc.
            col.title = col.title.replace(/Dec_year(\d+)/gi, (_match: string, n: string) => {
                const offset = parseInt(n, 10) - 1;
                return String(taxYear - offset);
            });

            // Also handle {tax_year} style templates
            col.title = col.title.replace(/\{tax_year\}/gi, String(taxYear));

            // Evaluate simple arithmetic in brackets: [2024 - 1] → 2023, [2024 + 1] → 2025
            col.title = col.title.replace(/\[(\d+)\s*([+\-])\s*(\d+)\]/g, (_m: string, a: string, op: string, b: string) => {
                return String(op === '+' ? parseInt(a, 10) + parseInt(b, 10) : parseInt(a, 10) - parseInt(b, 10));
            });

            // Convert remaining underscores to spaces in the title
            col.title = col.title.replace(/_/g, ' ');
        });
    }
}

